import { cloneElement, ReactElement, ReactNode } from 'react';

import cn from '@/helpers/utils/classNames';

import { useDropDownContext } from '../context';
import DropdownLink, { DropdownLinks } from '../DropdownLink';

type Headline = string | ReactNode;

interface ColumnProps {
  className?: string;
  headline: Headline;
  children: ReactElement<DropdownLinks>[];
}

const isString = (b: Headline): b is string => {
  return ((b as string) || {}).length !== undefined;
};

type ArraySizeOfTwo<T> = [T, T];

export type DropdownColumn = ArraySizeOfTwo<ColumnProps>;

function Column(props: ColumnProps) {
  const { headline, children, className } = props;

  const half = Math.ceil(children.length / 2);

  const firstHalf = children.slice(0, half);
  const secondHalf = children.slice(half);

  return (
    <div className={cn('v2-w-max v2-p-6 v2-px-6', className)}>
      <div className="v2-text-lg">
        {isString(headline) ? (
          <p className="v2-mb-[8px] v2-leading-[18px]">
            {headline.split(' ').map((word, index) => {
              if (index + 1 === headline.split(' ').length)
                return (
                  <b key={word} className="v2-font-sweet-sans-pro-semi-bold v2-capitalize v2-text-sm">
                    {word}
                  </b>
                );
              return (
                <span key={word} className="v2-font-normal v2-font-sweet-sans-pro v2-text-sm v2-text-black">
                  {word}&nbsp;
                </span>
              );
            })}
          </p>
        ) : (
          headline
        )}
        <hr className="v2-mb-[10px] v2-w-8 v2-border v2-border-primary" />
      </div>
      <div className="v2-grid v2-grid-cols-2 v2-gap-[10px]">
        <ul>{firstHalf}</ul>
        <ul>{secondHalf}</ul>
      </div>
    </div>
  );
}

export interface DropdownTwoColumnProps {
  className?: string;
  children: ArraySizeOfTwo<ReactElement<ColumnProps>>;
}

export default function DropdownTwoColumn(props: DropdownTwoColumnProps) {
  useDropDownContext();
  const { children, className } = props;

  return (
    <div
      className={cn(
        'v2-absolute v2-z-50 v2-mt-[19px] v2-rounded-[4px] v2-flex v2-w-max v2-min-w-[600px] v2-bg-white v2-shadow-[0_-5px_15px_0px_rgba(0,0,0,0.1)]',
        className
      )}
    >
      {cloneElement(children[0], { className: 'v2-bg-gray-50' })}
      {children[1]}
    </div>
  );
}

Column.ColumnItem = DropdownLink;
DropdownTwoColumn.Column = Column;
